/**
 * 客户端加密工具
 * 优先使用 @bitwarden/sdk-internal（Argon2id），失败时降级到 Web Crypto API（PBKDF2）
 * 零知识原则：所有加密/解密在客户端完成，服务端只存储加密后的数据
 */

import type { KdfConfig, DecryptedLoginData } from '@valuewarden/shared';
import { DEFAULT_KDF_CONFIG, IV_LENGTH, TAG_LENGTH } from '@valuewarden/shared';

// Bitwarden SDK 类型（内部包 API 可能变化，用宽松类型）
interface BitwardenSdk {
  argon2?: (password: string, salt: string, iterations: number, memory: number, parallelism: number) => Promise<Uint8Array>;
  [key: string]: unknown;
}

// 尝试导入 Bitwarden SDK，失败时降级到 Web Crypto API
let bitwardenSdk: BitwardenSdk | null = null;
let sdkInitialized = false;
let sdkInitPromise: Promise<void> | null = null;

async function initSdk(): Promise<void> {
  if (sdkInitialized) return;
  if (sdkInitPromise) return sdkInitPromise;

  sdkInitPromise = (async () => {
    try {
      const mod = await import('@bitwarden/sdk-internal');
      bitwardenSdk = mod as BitwardenSdk;
      sdkInitialized = true;
      console.info('[Crypto] Bitwarden SDK loaded successfully');
    } catch (error) {
      console.warn('[Crypto] Failed to load Bitwarden SDK, falling back to Web Crypto API:', error);
      bitwardenSdk = null;
      sdkInitialized = true;
    }
  })();

  return sdkInitPromise;
}

// ========== 工具函数 ==========

/** 创建指定长度的 Uint8Array<ArrayBuffer>（兼容 TS 5.7+ 类型检查） */
function createUint8Array(length: number): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new ArrayBuffer(length));
}

/** 生成随机字节 */
export function generateRandomBytes(length: number): Uint8Array<ArrayBuffer> {
  const bytes = createUint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** 生成随机盐值（Base64） */
export function generateSalt(): string {
  return bufferToBase64(generateRandomBytes(16));
}

/** Buffer 转 Base64 */
export function bufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Base64 转 Buffer */
export function base64ToBuffer(base64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = createUint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** UTF-8 字符串转 Buffer */
export function stringToBuffer(str: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(str) as Uint8Array<ArrayBuffer>;
}

/** Buffer 转 UTF-8 字符串 */
export function bufferToString(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  return new TextDecoder().decode(bytes);
}

// ========== KDF（密钥派生） ==========

/**
 * 使用 Argon2id 派生主密码哈希
 * 这是登录时客户端必须执行的步骤
 */
export async function hashPassword(
  password: string,
  salt: string,
  kdfConfig: KdfConfig = DEFAULT_KDF_CONFIG,
): Promise<string> {
  await initSdk();

  if (bitwardenSdk && typeof bitwardenSdk.argon2 === 'function') {
    try {
      // 使用 Bitwarden SDK 的 Argon2 实现
      const hash = await bitwardenSdk.argon2(
        password,
        salt,
        kdfConfig.iterations,
        kdfConfig.memory || 64 * 1024,
        kdfConfig.parallelism || 4,
      );
      if (hash instanceof Uint8Array) {
        return bufferToBase64(hash);
      }
      console.warn('[Crypto] Bitwarden SDK argon2 returned unexpected type, falling back');
    } catch (error) {
      console.warn('[Crypto] Bitwarden SDK argon2 failed, falling back:', error);
    }
  }

  // 降级：使用 Web Crypto API 的 PBKDF2
  return await pbkdf2Hash(password, salt, kdfConfig.iterations);
}

/** PBKDF2 哈希（降级方案） */
async function pbkdf2Hash(password: string, salt: string, iterations: number): Promise<string> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    stringToBuffer(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  const hashBuffer = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: base64ToBuffer(salt),
      iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    256,
  );

  return bufferToBase64(hashBuffer);
}

// ========== 对称加密（AES-GCM） ==========

/**
 * 加密数据（AES-256-GCM）
 * 返回加密后的数据、IV 和认证标签
 */
export async function encryptData(
  data: string,
  key: CryptoKey,
): Promise<{ encryptedData: string; iv: string; tag: string }> {
  const iv = generateRandomBytes(IV_LENGTH);

  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: TAG_LENGTH * 8,
    },
    key,
    stringToBuffer(data),
  );

  // AES-GCM 的输出是 密文 + tag，需要分离
  const encryptedBytes = new Uint8Array(encryptedBuffer);
  const tagLength = TAG_LENGTH;

  const ciphertext = createUint8Array(encryptedBytes.length - tagLength);
  ciphertext.set(encryptedBytes.slice(0, encryptedBytes.length - tagLength));

  const tag = createUint8Array(tagLength);
  tag.set(encryptedBytes.slice(encryptedBytes.length - tagLength));

  return {
    encryptedData: bufferToBase64(ciphertext),
    iv: bufferToBase64(iv),
    tag: bufferToBase64(tag),
  };
}

/**
 * 解密数据（AES-256-GCM）
 */
export async function decryptData(
  encryptedData: string,
  iv: string,
  tag: string,
  key: CryptoKey,
): Promise<string> {
  const ciphertext = base64ToBuffer(encryptedData);
  const ivBuffer = base64ToBuffer(iv);
  const tagBuffer = base64ToBuffer(tag);

  // 合并密文和 tag
  const combined = createUint8Array(ciphertext.length + tagBuffer.length);
  combined.set(ciphertext, 0);
  combined.set(tagBuffer, ciphertext.length);

  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ivBuffer,
      tagLength: TAG_LENGTH * 8,
    },
    key,
    combined,
  );

  return bufferToString(decryptedBuffer);
}

// ========== 密钥管理 ==========

/**
 * 从主密码派生加密密钥
 * 用于加密/解密密码库数据
 */
export async function deriveEncryptionKey(
  password: string,
  salt: string,
  kdfConfig: KdfConfig = DEFAULT_KDF_CONFIG,
): Promise<CryptoKey> {
  // 先派生主密钥
  const masterKeyHash = await hashPassword(password, salt, kdfConfig);

  // 从主密钥派生加密密钥（HKDF）
  const masterKeyBuffer = base64ToBuffer(masterKeyHash);
  const masterKey = await crypto.subtle.importKey('raw', masterKeyBuffer, 'HKDF', false, [
    'deriveKey',
  ]);

  const encryptionKey = await crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      salt: stringToBuffer('valuewarden-encryption-key'),
      info: stringToBuffer('AES-GCM encryption'),
      hash: 'SHA-256',
    },
    masterKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );

  return encryptionKey;
}

/**
 * 生成随机加密密钥（用于新用户的用户密钥）
 */
export async function generateUserKey(): Promise<CryptoKey> {
  return await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * 导出密钥为 Base64
 */
export async function exportKey(key: CryptoKey): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', key);
  return bufferToBase64(raw);
}

/**
 * 从 Base64 导入密钥
 */
export async function importKey(base64Key: string): Promise<CryptoKey> {
  const raw = base64ToBuffer(base64Key);
  return await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ]);
}

// ========== 密码库数据序列化 ==========

/**
 * 加密登录类型数据
 */
export async function encryptLoginData(
  data: DecryptedLoginData,
  key: CryptoKey,
): Promise<{ encryptedData: string; iv: string; tag: string }> {
  const json = JSON.stringify(data);
  return encryptData(json, key);
}

/**
 * 解密登录类型数据
 */
export async function decryptLoginData(
  encryptedData: string,
  iv: string,
  tag: string,
  key: CryptoKey,
): Promise<DecryptedLoginData> {
  const json = await decryptData(encryptedData, iv, tag, key);
  return JSON.parse(json) as DecryptedLoginData;
}

// ========== 密码生成器 ==========

// ========== 救援码 ==========

/** Crockford Base32 字符集（去除易混淆的 I、L、O、U） */
const RESCUE_CODE_CHARSET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const RESCUE_CODE_GROUP_LENGTH = 8;
const RESCUE_CODE_GROUPS = 4;

/**
 * 生成救援码（格式 XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX，约 160 bits 熵）
 * 救援码明文仅在生成时展示一次，用于忘记主密码时重置
 */
export function generateRescueCode(): string {
  const groups: string[] = [];
  for (let g = 0; g < RESCUE_CODE_GROUPS; g++) {
    let group = '';
    const bytes = generateRandomBytes(RESCUE_CODE_GROUP_LENGTH);
    for (let i = 0; i < RESCUE_CODE_GROUP_LENGTH; i++) {
      group += RESCUE_CODE_CHARSET[bytes[i] % RESCUE_CODE_CHARSET.length];
    }
    groups.push(group);
  }
  return groups.join('-');
}

/**
 * 规范化用户输入的救援码：去除分隔符与空白、大写、映射易混淆字符（Crockford 解码规则），
 * 重组为标准 4 组 × 8 字符格式。用户输入时大小写/分隔符不一致也能正确匹配。
 */
export function normalizeRescueCode(input: string): string {
  let cleaned = input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
    .replace(/U/g, 'V');

  // 截取前 32 个字符（4 组 × 8）
  cleaned = cleaned.slice(0, RESCUE_CODE_GROUPS * RESCUE_CODE_GROUP_LENGTH);

  const groups: string[] = [];
  for (let g = 0; g < RESCUE_CODE_GROUPS; g++) {
    groups.push(cleaned.slice(g * RESCUE_CODE_GROUP_LENGTH, (g + 1) * RESCUE_CODE_GROUP_LENGTH));
  }
  return groups.filter(Boolean).join('-');
}

// ========== 密码生成器（旧区块） ==========

/**
 * 生成随机密码
 */
export function generatePassword(length = 16, options?: {
  uppercase?: boolean;
  lowercase?: boolean;
  numbers?: boolean;
  symbols?: boolean;
}): string {
  const opts = {
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: false,
    ...options,
  };

  let charset = '';
  if (opts.uppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (opts.lowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
  if (opts.numbers) charset += '0123456789';
  if (opts.symbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

  if (charset.length === 0) charset = 'abcdefghijklmnopqrstuvwxyz';

  const bytes = generateRandomBytes(length);
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset[bytes[i] % charset.length];
  }
  return password;
}
